import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChildren, QueryList,
	ViewChild, ViewEncapsulation, OnDestroy } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule,
	Validators } from '@angular/forms';
import { MatOption, MatOptionModule } from '@angular/material/core';
import { catchError, forkJoin, map, Observable, of, Subscription, tap } from 'rxjs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@copo/mat-datepicker-new';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgFor, NgClass, DecimalPipe, TitleCasePipe, KeyValuePipe } from '@angular/common';

import { CodeValue } from '../../../../shared/models/boc-available-value';
import { CodeValue as CodeValueType } from '../../../../shared/models/code-value';
import { UserService } from '../../../../shared/services/user.service';
import { EorderProduct, TemplateDescription, TemplateVariableArray, FORM_SHEET_CODE,
	TemplateVariableString,	VariableField, PRODUCT_TYPE_F, PRODUCT_TYPE_NF, VariableFieldUpdated,
	FIRST_ROW_FIELDS, FIELD_SHIPTO_PARTY, FIELD_STEEL_GRADE, DEFAULT_SERVICE_CODE, FIELD_TEMPLATE_NUMBER,
	ProductFamily, TemplateVariable } from '../../../models/eorder';
import { FormEorder, FormProduct } from '../../../models/form-eorder';
import { EorderWizardStep } from '../../../models/eorder-wizard-state';
import { CharacteristicValueSimpleObject, ProductConfiguration, CharacteristicValue,
	TemplateVariableValues, FieldValueObject } from '../../../../shared/models/product';
import { ConfigurationService } from '../../../../shared/services/configuration.api.service';
import { EorderWizardService } from '../../../services/eorder-wizard.service';
import { WizardPageContent } from '../wizard-page-content';
import { EorderWizardStepperService } from '../../../services/eorder-wizard-stepper.service';
import { EorderApiService } from '../../../services/eorder.api.service';
import { ApplyAllComponent } from '../apply-all/apply-all.component';
import { fieldTransitions } from '../../../../shared/animations/field-transitions';
import { InputNumberDirective } from '../../../../shared/directives/input-number.directive';
import { UNDERSCORE } from '../../../../shared/utils/constant';
import {
	NAMESPACE_RTPC_FIELD,
	ProductCharacteristicsTranslationService
} from '../../../../shared/services/product-characteristics-translation.services';
import { TranslationService } from '../../../../shared/services/translation.service';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { CopoDatepickerInputDirective } from '../../../../shared/directives/date-picker.directive';
import { SortOptionsPipe } from '../../../../shared/pipes/sorting.pipe';
import { BusinessType } from '../../../../shared/models/business-type';
import { TRANSLATION_TYPE_SIDE } from '../../../../shared/models/data-with-translations';

@Component({
	selector: 'copo-view-cart',
	templateUrl: './view-cart.component.html',
	styleUrl: './view-cart.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	animations: [
		trigger('deleteItemTrigger', [
			transition(':leave', [
				animate(750, style({
					opacity: 0,
					height: 0,
				}))
			])
		]), fieldTransitions
	],
	imports: [
		NgIf,
		FormsModule,
		ReactiveFormsModule,
		NgFor,
		MatIconModule,
		TranslateModule,
		NgClass,
		MatTooltipModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatOptionModule,
		CopoDatepickerInputDirective,
		MatDatepickerModule,
		MatButtonModule,
		MatMenuModule,
		MatDividerModule,
		MatExpansionModule,
		LoadingSpinnerComponent,
		DecimalPipe,
		InputNumberDirective,
		SortOptionsPipe,
		ApplyAllComponent,
		TitleCasePipe,
		KeyValuePipe
	]
})

export class ViewCartComponent extends WizardPageContent implements OnInit, OnDestroy {
	@ViewChild('shipToField', { read: ElementRef }) shipToField!: ElementRef<MatOption>;
	@ViewChildren('rows') rows!: QueryList<ElementRef<HTMLElement>>;

	readonly UNDERSCORE = UNDERSCORE;
	soldto?: string;
	isSheet = false;

	isExpanded: boolean[] = [];
	tModes: CodeValue[];
	businessTypes: BusinessType[] = [];
	serviceLevels: CodeValue[];
	xcarbParamList: CodeValue[];
	rddTypes: CodeValueType[] = [
		{ code: 'D', value: 'Day' },
		{ code: 'W', value: 'Week' }
	];

	variableconfig = { codeProp: 'code', valueProp: 'value' };
	configForBoc = { codeProp: 'businessId', valueProp: 'name' };
	calStartDate!: Date;
	calEndDate!: Date;
	readonly form: FormEorder;
	isLoading = true;
	displayShipTo = false;
	isTmodeUpdated: boolean[] = [];
	currencyList?: CodeValue[];
	isShipToUpdated: boolean[] = [];
	isBusinessTypeUpdated: boolean[] = [];
	isServiceLevelUpdated: boolean[] = [];
	isxcarbParamUpdated: boolean[] = [];
	isRddUpdated: boolean[] = [];
	isRddTypeUpdated: boolean[] = [];
	templatesInLoading = new Map<number, number[]>();
	inputForApplyAll = new Map<string, string>();
	productFamily?: ProductFamily;
	simulationSubscriptionArr?: Subscription[] = [];
	private variableDataSub?: Subscription;
	private readonly isVariableFieldUpdatedArr: VariableFieldUpdated[] = [];

	private readonly VIEW_STEP_NUM = 1;

	constructor(
		private readonly eorderWizardService: EorderWizardService,
		private readonly userService: UserService,
		private readonly eorderStepperService: EorderWizardStepperService,
		private readonly configurationService: ConfigurationService,
		private readonly eorderApiService: EorderApiService,
		private readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService,
		private readonly translationService: TranslationService,
		public changeDetectorRef: ChangeDetectorRef
	) {
		super(EorderWizardStep.PRODUCTMODIFICATION, eorderWizardService.getState());
		this.simulationSubscriptionArr = [];
		this.currencyList = this.bocAvailableValues('CURRENCY');
		this.form = this.eorderWizardService.getStateEorderForm();
		this.productFamily = this.eorderWizardService.getState().ui.getSelectedProductFamily();
		const formProducts = this.form.getFormProducts();
		const controlArray = <FormArray> formProducts;
		this.tModes = this.userService.getBocAvailableValuesBasedOnCode('EORDR', 'TMODE') || [];
		this.tModes = this.tModes.filter(item => item.code !== 'DEFAU');
		if (
			this.userService.user.hasServiceParameterEqualsTo('EORDR', 'BusinessType', 'Open')
			&& this.businessTypes.length === 0
		) {
			this.userService.getBusinessTypes(<string> this.form.getFormSoldTo().value).then(businessTypes => {
				this.businessTypes = businessTypes;
				this.businessTypes.unshift({
					businessCode: '',
					businessDescription: 'None'
				});
			}).catch((error: unknown) => {
				console.error('Error fetching business types:', error);
			});
		}
		this.serviceLevels = this.userService.getBocAvailableValuesBasedOnCode('EORDR', 'SERVICELEVEL') || [];
		this.xcarbParamList = this.userService.getBocAvailableValuesBasedOnCode('EORDR', 'XCARB') || [];
		if (this.xcarbParamList.length > 0 && !this.xcarbParamList.some(param => param.code === '')) {
			this.xcarbParamList.unshift({
				code: '',
				description: 'None',
				isDefault: true
			});
		}
		const defaultTMmodes = this.tModes.filter(e => e.isDefault);
		const defaultXCarb = this.xcarbParamList.filter(e => e.isDefault);
		const defaultServiceLevel = this.getDefaultServiceLevel();
		const defaultBusinessType = [
			{
				businessCode: '',
				businessDescription: 'None'
			}
		];
		for (let i = 0; i < controlArray.length; i++) {
			if (defaultTMmodes.length > 0) {
				this.setDefaultValue('transportMode', i, defaultTMmodes[0].code);
			}
			if (defaultXCarb.length > 0) {
				this.setDefaultValue('xcarb', i, defaultXCarb[0].code);
			}
			if (defaultBusinessType.length > 0) {
				this.setDefaultValue('businessType', i, defaultBusinessType[0].businessCode);
			}
			this.setDefaultValue('serviceLevel', i, defaultServiceLevel);
		}

		const orderItems: EorderProduct[] = [];
		let j = 0;
		for (const formProduct of formProducts.getFormProducts()) {
			orderItems.push(formProduct.product);
			this.isExpanded[j] = true;
			this.isTmodeUpdated[j] = false;
			this.isShipToUpdated[j] = false;
			this.isBusinessTypeUpdated[j] = false;
			this.isServiceLevelUpdated[j] = false;
			this.isxcarbParamUpdated[j] = false;
			this.isRddUpdated[j] = false;
			this.isRddTypeUpdated[j] = false;
			this.isVariableFieldUpdatedArr[j] = new Map<string, boolean>();
			j++;
		}
		for (const [index, orderItem] of orderItems.entries()) {
			this.fetchData(orderItem, index);
		}
		this.setCalendarStartEndDate();
		this.isLoading = false;
	}

	get productResultControl() {
		return (<FormArray> this.form.controls['products']).controls;
	}

	ngOnInit(): void {
		this.eorderStepperService.onStepChanged.emit({ step: this.VIEW_STEP_NUM });
		const soldtoShiptoList = this.eorderApiService.filteredSoldtoShiptoList;
		const selSoldtoId = <string> this.form.getFormSoldTo().value;
		if (soldtoShiptoList) {
			const selectedSoldtoList = soldtoShiptoList.filter(v => v.businessId === selSoldtoId);
			for (const soldtoList of selectedSoldtoList) {
				this.soldto = `${ soldtoList.name } - ${ soldtoList.city }`;
			}
		}
		if (this.form.getFormValue() === FORM_SHEET_CODE) {
			this.isSheet = true;
		}
		this.prepareDefaultValuesForApplyForAll();
	}

	ngOnDestroy() {
		if (this.variableDataSub) {
			this.variableDataSub.unsubscribe();
		}
		if (this.simulationSubscriptionArr) {
			for (const simulationSubscription of this.simulationSubscriptionArr) {
				simulationSubscription.unsubscribe();
			}
		}
	}

	getDefaultServiceLevel() {
		const defaultServiceLevels = this.serviceLevels.filter(e => e.isDefault);
		return defaultServiceLevels.length === 1
			? defaultServiceLevels[0].code
			: DEFAULT_SERVICE_CODE;
	}

	simulationCallOnValueChange(index: number) {
		this.updateBeforeSimulationCall(index);
		this.prepareDataAndSimulateA2Cproduct(index);
	}

	prepareDataAndSimulateA2Cproduct(index: number) {
		const templateNumber = this.getTemplateNumber(index);
		if (!templateNumber) {
			return;
		}
		if (this.simulationSubscriptionArr !== undefined) {
			this.simulationSubscriptionArr[index]?.unsubscribe();
		}
		const valuesPossiblePerField = this.getValuesPossiblePerField(templateNumber);
		const variableValuesExcludingFirstRow = this.filterVariablesExcludingFirstRow(valuesPossiblePerField);
		if (this.validateFieldsForSimulation(index)) {
			const controlArray = <FormArray> this.form.get('products');
			const formProduct = <FormProduct> controlArray.controls[index];
			let productConfigObj = <ProductConfiguration>{};
			formProduct.setErrors(null);
			formProduct.markAsUntouched();
			const simulationCompletedFlagControl = <FormControl> formProduct.get('isSimulationCompleted');
			const configErrMsgControl = <FormControl>controlArray.controls[index].get('configErrMsg');
			if (!simulationCompletedFlagControl.value && configErrMsgControl.value === '') {
				const templateAndDetail = this.eorderWizardService.getState().ui.getTemplateNumberDetails(
					templateNumber
				);
				productConfigObj = this.setProperty(productConfigObj, FIELD_TEMPLATE_NUMBER, templateNumber);
				const selectedProduct = <FormProduct>controlArray.controls[index].value;
				for (const [fieldKey, value] of Object.entries(selectedProduct)) {
					const templateFields = Object.getOwnPropertyNames(templateAndDetail?.properties);
					if ((templateFields.includes(fieldKey) || templateAndDetail?.listOfVariables.includes(fieldKey))
						&& value !== null && value !== '' && value !== undefined) {
						let fieldValue;
						switch (typeof value) {
						case 'string':
							fieldValue = String(value);
							break;
						case 'number':
							fieldValue = Number(value);
							break;
						default:
							break;
						}
						if (fieldValue && fieldValue !== '') {
							if (fieldKey === FIELD_SHIPTO_PARTY) {
								fieldValue = String(fieldValue).padStart(10, '0');
							}
							productConfigObj = this.setProperty(productConfigObj, fieldKey, fieldValue);
						}
					}
				}
				for (const variableField of variableValuesExcludingFirstRow) {
					const formGroupcontrol = controlArray.controls[index]
						.get('variableFormGroup')?.get(variableField.keys);
					const keyField = variableField.keys;
					if (formGroupcontrol) {
						if (formGroupcontrol instanceof FormArray) {
							const arrayValues = [];
							for (const control of formGroupcontrol.controls) {
								if (control.value && control.value !== '') {
									arrayValues.push({ [keyField]: String(control.value) });
								}
							}
							productConfigObj = this
								.setProperty(productConfigObj, variableField.parentVariableName, arrayValues);
						} else if (formGroupcontrol.value && formGroupcontrol.value !== '') {
							const value = variableField.type === 'number'
								? Number(formGroupcontrol.value) : String(formGroupcontrol.value);
							productConfigObj = this.setProperty(productConfigObj, keyField, value);
						}
					}
				}
				const simulationSubscription = this.simulateA2Cproduct(productConfigObj, index).subscribe({
					next: simulationResponse => {
						if (simulationResponse !== undefined) {
							const configurationErrors = simulationResponse.errors ? [...simulationResponse.errors] : [];
							if (configurationErrors.length === 0) {
								this.updateSimulationStatus(index);
								if (simulationResponse.configuration) {
									this.updateSimulationData(index, simulationResponse.configuration);
								}
							} else {
								this.updateSimulationStatus(index, configurationErrors);
							}
						} else {
							this.updateSimulationStatus(
								index,
								undefined,
								this.translationService.getTR('global.unexpected.error.identified')
							);
						}
					},
					error: () => {
						this.updateSimulationStatus(
							index,
							undefined,
							this.translationService.getTR('global.unexpected.error.identified')
						);
					}
				});
				if (this.simulationSubscriptionArr !== undefined) {
					this.simulationSubscriptionArr.splice(index, 1, simulationSubscription);
				}
			} else {
				const loaderControl = <FormControl> formProduct.get('loader');
				loaderControl.setValue('false');
			}
		}
	}

	simulateA2Cproduct(configuration: ProductConfiguration | FieldValueObject, index: number) {
		const controlArray = <FormArray> this.form.get('products');
		const configErrMsgControl = <FormControl> controlArray.controls[index].get('configErrMsg');
		configErrMsgControl.setValue('');
		const loaderControl = <FormControl> controlArray.controls[index].get('loader');
		loaderControl.setValue('true');
		return this.configurationService
			.simulateTemplateProduct(
				configuration,
				TRANSLATION_TYPE_SIDE
			)
			.pipe(
				catchError((err: unknown) => {
					console.error('unable to simulate', err);
					return of(undefined);
				})
			);
	}

	validateFieldsForSimulation(index: number) {
		const controlArray = <FormArray> this.form.controls['products'];
		const productForm = <FormProduct> controlArray.controls[index];
		const steelGradeControl = <FormControl>productForm.get('steelGrade');
		const thicknessControl = <FormControl>productForm.get('thicknessValue');
		const widthControl = <FormControl>productForm.get('widthValue');
		const lengthControl = <FormControl>productForm.get('lengthValue');
		const shipToPartyControl = <FormControl>productForm.get('shipToParty');

		return (!this.isVariable(index, 'steelGrade') || steelGradeControl.valid)
			&& (!this.isVariable(index, 'thicknessValue') || thicknessControl.valid)
			&& (!this.isVariable(index, 'widthValue') || widthControl.valid)
			&& (!this.isVariable(index, 'shipToParty') || shipToPartyControl.valid)
			&& (!this.isSheet || !this.isVariable(index, 'lengthValue') || lengthControl.valid)
			&& this.validateVariableFormGroup(index);
	}

	validateVariableFormGroup(index: number) {
		const controlArray = <FormArray> this.form.controls['products'];
		const productForm = <FormProduct> controlArray.controls[index];
		for (const key of Object.keys(productForm.getVariableFormGroup().controls)) {
			const formGroupControl = productForm.getVariableFormGroup().get(key);
			if (formGroupControl) {
				if (formGroupControl instanceof FormArray) {
					for (const control of formGroupControl.controls) {
						if (!control.valid) {
							return false;
						}
					}
				} else if (!formGroupControl.valid) {
					return false;
				}
			}
		}
		return true;
	}

	checkError(): boolean {
		const controlArray = <FormArray> this.form.controls['products'];
		if (this.form.getFormProducts().length === 0) {
			return true;
		}
		let indexToScroll = -1;
		let i = 0;
		for (const control of controlArray.controls) {
			if (control.invalid) {
				control.markAllAsTouched();
				control.updateValueAndValidity();
				if (indexToScroll === -1) {
					indexToScroll = i;
				}
			}
			i++;
		}
		if (indexToScroll !== -1) {
			const rowItems = this.rows.toArray();
			if (rowItems[indexToScroll]) {
				rowItems[indexToScroll].nativeElement.scrollIntoView();
			}
			return true;
		}
		return false;
	}

	checkErrorForDraft() {
		return false;
	}

	expandPanel(index: number) {
		this.isExpanded[index] = !this.isExpanded[index];
	}

	getExpandPanelStatus(index: number) {
		return this.isExpanded[index];
	}

	filterVariablesExcludingFirstRow(fieldKeyValues: VariableField[]) {
		return fieldKeyValues
			.filter(fieldKeyValue =>
				!FIRST_ROW_FIELDS.includes(fieldKeyValue.keys))
			.sort((a, b) => a.keys.localeCompare(b.keys));
	}

	filterVariablesFirstRow(fieldKeyValues: VariableField[]) {
		return fieldKeyValues
			.filter(fieldKeyValue =>
				FIRST_ROW_FIELDS.includes(fieldKeyValue.keys))
			.sort((a, b) => a.keys.localeCompare(b.keys));
	}

	getOptionalToleranceFields(index: number) {
		const templateNumber = this.getTemplateNumber(index);
		if (templateNumber) {
			return this.eorderWizardService.getOptionalToleranceFields(templateNumber);
		}
		return new Map<string, VariableField[]>();
	}

	getVariableFormArray(index: number, field: string) {
		const controlArray = <FormArray> this.form.get('products');
		const variableFormGroup = <FormGroup> controlArray.controls[index].get('variableFormGroup');
		return (<FormArray> variableFormGroup.get(field)).controls;
	}

	getValuesPossiblePerField(templateNumber: number): VariableField[] {
		return this.eorderWizardService.getValuesPossiblePerField(templateNumber);
	}

	isVariable(index: number, variableName: string): boolean {
		const templateNumber = this.getTemplateNumber(index);
		if (templateNumber) {
			const valuesPossiblePerField = this.getValuesPossiblePerField(templateNumber);
			return valuesPossiblePerField.some(v => v.keys === variableName);
		}
		return false;
	}

	loadAllVariableValues(template: TemplateDescription) {
		const variableValueObservables: Observable<boolean>[] = [];
		for (const variableName of Object.getOwnPropertyNames(template.properties)) {
			const variable = template.properties[variableName];
			this.loadAllVariableValuesByType(template, variableName, variable, variableValueObservables);
		}
		return forkJoin(variableValueObservables).pipe(map(results => results.filter(isOk => !isOk).length === 0));
	}

	fetchData(orderItem: EorderProduct, index: number) {
		if (orderItem.templateNumber && orderItem.productType !== PRODUCT_TYPE_F) {
			const templateAndDetail = this.eorderWizardService.getState().ui.getTemplateNumberDetails(
				orderItem.templateNumber
			);
			if (!templateAndDetail) {
				return;
			}
			const indexListInTemplate = this.templatesInLoading.get(orderItem.templateNumber);
			if (indexListInTemplate) {
				indexListInTemplate.push(index);
				return;
			}
			this.templatesInLoading.set(orderItem.templateNumber, [index]);
			this.variableDataSub = this.loadAllVariableValues(templateAndDetail)
				.pipe(map(valuesLoaded => valuesLoaded ? templateAndDetail : undefined))
				.subscribe({
					next: templateAndDetailRes => {
						if (templateAndDetailRes && orderItem.templateNumber) {
							this.eorderWizardService.populateValuesPossiblePerField(orderItem.templateNumber);
							const indexListInTemplateLcl = this.templatesInLoading.get(orderItem.templateNumber);
							if (indexListInTemplateLcl !== undefined) {
								for (const indexToProccess of indexListInTemplateLcl) {
									this.prepareDataAndSimulateA2Cproduct(indexToProccess);
								}
								this.eorderWizardService.populateVariablesForThirdRow(orderItem.templateNumber);
								this.eorderWizardService.populateVariablesForOptionalTolerance(
									orderItem.templateNumber
								);
								this.templatesInLoading.delete(orderItem.templateNumber);
							}
							const valuesPossiblePerField = this.getValuesPossiblePerField(orderItem.templateNumber);
							if (valuesPossiblePerField.length > 0 && !this.displayShipTo
								&& valuesPossiblePerField.some(v => v.keys === FIELD_SHIPTO_PARTY)) {
								this.displayShipTo = true;
							}
						}
					},
					error: (err: unknown) => {
						this.handleError('unexpected error when loading product edition card', err);
					}
				});
		} else {
			const controlArray = <FormArray> this.form.get('products');
			const formProduct = <FormProduct> controlArray.controls[index];
			formProduct.getSimulationCompleted().setValue('true');
			formProduct.product.isSimulationCompleted = 'true';
		}
	}

	setCalendarStartEndDate() {
		const calEndDate = new Date();
		calEndDate.setFullYear(calEndDate.getFullYear() + 2);
		this.calStartDate = new Date();
		this.calEndDate = calEndDate;
	}

	getControl(formIndex: number, controlName: string) {
		const controlArray = <FormArray> this.form.get('products');
		return <FormControl>controlArray.controls[formIndex].get(controlName);
	}

	getVariableFormGroupControl(formIndex: number, controlName: string) {
		const controlArray = <FormArray> this.form.get('products');
		return <FormControl> controlArray.controls[formIndex].get('variableFormGroup')?.get(controlName);
	}

	getSteelGradeDescription(formIndex: number) {
		const products = <FormArray> this.form.get('products');
		const formProduct = <FormProduct> products.controls[formIndex];
		const product = formProduct.getProduct();
		return product.steelGradeDescription || ' ';
	}

	getPossibleValues(formIndex: number, variableName: string): CodeValueType[] {
		const templateNumber = this.getTemplateNumber(formIndex);
		if (templateNumber) {
			const valuesPossiblePerField = this.getValuesPossiblePerField(templateNumber);
			return valuesPossiblePerField.filter(v => v.keys === variableName).flatMap(v => v.values);
		}
		return [];
	}

	getValueByControlName(formIndex: number, controlName: string) {
		const controlArray = <FormArray> this.form.get('products');
		const formControl = <FormControl> controlArray.controls[formIndex].get(controlName);
		return <string>formControl.value;
	}

	getSimulationStatus(formIndex: number) {
		const controlArray = <FormArray> this.form.get('products');
		const simulationStatusControl = <FormControl> controlArray.controls[formIndex].get('isSimulationCompleted');
		let response = 'svg-fill-yellow';
		if (simulationStatusControl.value === 'true') {
			response = 'svg-fill-green';
		} else {
			const simulationErrorControl = <FormControl> controlArray.controls[formIndex].get('configErrMsg');
			if (simulationErrorControl.value) {
				response = 'svg-fill-red';
			}
		}
		return response;
	}

	isFullyConfigured(formIndex: number) {
		const products = <FormArray> this.form.get('products');
		const formProduct = <FormProduct> products.controls[formIndex];
		const product = formProduct.getProduct();
		return product.productType === PRODUCT_TYPE_F;
	}

	getShipTo(formIndex: number) {
		const shipToId = Number(this.getValueByControlName(formIndex, 'shipToParty'));
		if (this.getShipToList().length > 0) {
			const bocClientFullShipTo = this.getShipToList().find(v => v.businessId === String(shipToId));
			if (bocClientFullShipTo) {
				return `${ bocClientFullShipTo.name } / ${ bocClientFullShipTo.city }`;
			}
		}
		return '';
	}

	getShipToList() {
		return this.eorderWizardService.shipToList || [];
	}

	getVariablesForThirdRow(index: number) {
		const templateNumber = this.getTemplateNumber(index);
		if (templateNumber) {
			return this.eorderWizardService.getVariablesForThirdRow(templateNumber);
		}
		return [];
	}

	getFieldLabel(fieldLabel: string) {
		return this.productCharacteristicsTranslationService
			.translate(`${ NAMESPACE_RTPC_FIELD }${ fieldLabel }`);
	}

	compareWithFn(field1: string, field2: string) {
		return field1 === field2;
	}

	getVariableFieldValue(formIndex: number, field: string) {
		const controlArray = <FormArray> this.form.get('products');
		const variableFormControl = <FormControl> controlArray.controls[formIndex].get('variableFormGroup')?.get(field);
		return <string>variableFormControl.value;
	}

	resetVariableFieldValue(formIndex: number, field: string) {
		const controlArray = <FormArray> this.form.get('products');
		const variableFormControl = <FormControl> controlArray.controls[formIndex].get('variableFormGroup')?.get(field);
		variableFormControl.setValue('');
		variableFormControl.markAsUntouched();
		this.simulationCallOnValueChange(formIndex);
	}

	getVariableFormArrayValueByIndex(formIndex: number, index: number, field: string) {
		const controlArray = <FormArray> this.form.get('products');
		const variableFormGroup = <FormGroup> controlArray.controls[formIndex].get('variableFormGroup');
		const variableFieldArray = <FormArray> variableFormGroup.get(field);
		return <string> variableFieldArray.controls[index].value;
	}

	checkIfConfigValueInVariableList(index: number, key: string, values?: CodeValueType[]) {
		const configData = this.getConfigData(index);
		if (configData?.[key]) {
			// eslint-disable-next-line @typescript-eslint/no-base-to-string
			const value = this.removeTranslationPrefix(String(configData[key]));
			return !values?.some(v => v.code === value);
		}
		return false;
	}

	getConfigValue(index: number, key: string) {
		const configData = this.getConfigData(index);
		if (configData?.[key]) {
			// eslint-disable-next-line @typescript-eslint/no-base-to-string
			return this.removeTranslationPrefix(String(configData[key]));
		}
		return '';
	}

	getTranslationForCodeByIndex(code: string, index: number) {
		const configData = this.getConfigData(index);
		if (configData?.[code]) {
			// eslint-disable-next-line @typescript-eslint/no-base-to-string
			return this.productCharacteristicsTranslationService.translate(String(configData[code]));
		}
		return '';
	}

	getConfigData(index: number) {
		const controlArray = <FormArray> this.form.get('products');
		const configurationControl = <FormControl>controlArray.controls[index].get('configurationNumber');
		const configurationNumber = String(configurationControl.value);
		if (configurationNumber && this.eorderWizardService.getState().ui.hasConfigurationData(configurationNumber)) {
			return this.eorderWizardService.getState().ui.getConfigurationData(configurationNumber);
		}
		return null;
	}

	/**
	 * Retrieves the configuration value for a multi-item field.
	 * ex: "labelContent": [ - contentParentFieldName
	 * {
	 * "content": "" - contentChildFieldName
	 * }
	 * ]
	 * @param contentChildFieldName - The name of the child field.
	 * @param formIndex - The index of the form.
	 * @param index - The index of the item within the multi-item field.
	 * @param contentParentFieldName - The name of the parent field.
	 * @returns The configuration value for the specified field.
	 */
	getConfigValueMultiItems(
		contentChildFieldName: string,
		formIndex: number,
		index: number,
		contentParentFieldName: string
	) {
		const configData = this.getConfigDataMultiItems(formIndex, contentParentFieldName);
		if (configData) {
			const arrayObject = <CharacteristicValueSimpleObject[]>configData;
			const value = arrayObject[index] ? arrayObject[index][contentChildFieldName] : '';
			return this.removeTranslationPrefix(String(value));
		}
		return '';
	}

	checkIfConfigValueInVariableListMultiItems(
		contentChildFieldName: string,
		formIndex: number,
		index: number,
		contentParentFieldName: string,
		values?: CodeValueType[]
	) {
		const configData = this.getConfigDataMultiItems(formIndex, contentParentFieldName);
		if (configData) {
			const arrayObject = <CharacteristicValueSimpleObject[]>configData;
			let value = arrayObject[index] ? arrayObject[index][contentChildFieldName] : undefined;
			if (value) {
				value = this.removeTranslationPrefix(String(value));
				return !values?.some(v => v.code === value);
			}
		}
		return false;
	}

	getTranslationForCodeByIndexMultiItems(
		contentChildFieldName: string,
		formIndex: number,
		index: number,
		contentParentFieldName: string
	) {
		const configData = this.getConfigDataMultiItems(formIndex, contentParentFieldName);
		if (configData) {
			const arrayObject = <CharacteristicValueSimpleObject[]>configData;
			const value = arrayObject[index] ? arrayObject[index][contentChildFieldName] : '';
			return this.productCharacteristicsTranslationService.translate(String(value));
		}
		return '';
	}

	getConfigDataMultiItems(formIndex: number, contentParentFieldName: string) {
		const controlArray = <FormArray> this.form.get('products');
		const configurationControl = <FormControl>controlArray.controls[formIndex].get('configurationNumber');
		const configurationNumber = String(configurationControl.value);
		if (configurationNumber && this.eorderWizardService.getState().ui.hasConfigurationData(configurationNumber)) {
			const configData = this.eorderWizardService.getState().ui.getConfigurationData(configurationNumber);
			if (configData?.[contentParentFieldName]) {
				return configData[contentParentFieldName];
			}
		}
		return undefined;
	}

	resetVariableFormArrayValueByIndex(formIndex: number, index: number, field: string) {
		const controlArray = <FormArray> this.form.get('products');
		const variableFormGroup = <FormGroup> controlArray.controls[formIndex].get('variableFormGroup');
		const variableFieldArray = <FormArray> variableFormGroup.get(field);
		variableFieldArray.controls[index].setValue('');
		variableFieldArray.controls[index].markAsUntouched();
	}

	addFieldToVariableFormArray(
		formIndex: number,
		maxItemsAllowed: number,
		field: string,
		isMandatory: boolean,
		isHidden: boolean
	) {
		const controlArray = <FormArray> this.form.get('products');
		const variableFormGroup = <FormGroup> controlArray.controls[formIndex].get('variableFormGroup');
		const control = <FormArray> variableFormGroup.get(field);
		if (control.length < maxItemsAllowed) {
			if (isMandatory && !isHidden) {
				control.push(new FormControl('', Validators.required));
			} else {
				control.push(new FormControl(''));
			}
		}
	}

	removeFieldFromVariableFormArray(formIndex: number, removeIndex: number, field: string) {
		const controlArray = <FormArray> this.form.get('products');
		const variableFormGroup = <FormGroup> controlArray.controls[formIndex].get('variableFormGroup');
		const control = <FormArray> variableFormGroup.get(field);
		control.removeAt(removeIndex);
	}

	getTemplateNumber(formIndex: number) {
		const products = <FormArray> this.form.get('products');
		const formProduct = <FormProduct> products.controls[formIndex];
		const product = formProduct.getProduct();
		return product.templateNumber;
	}

	removeProduct(index: number) {
		const control = <FormArray> this.eorderWizardService.getStateEorderForm().getFormProducts();
		control.removeAt(index);
		this.isExpanded.splice(index, 1);
		this.isTmodeUpdated.splice(index, 1);
		this.isBusinessTypeUpdated.splice(index, 1);
		this.isShipToUpdated.splice(index, 1);
		this.isServiceLevelUpdated.splice(index, 1);
		this.isxcarbParamUpdated.splice(index, 1);
		this.isRddUpdated.splice(index, 1);
		this.isRddTypeUpdated.splice(index, 1);
		this.isVariableFieldUpdatedArr.splice(index, 1);
	}

	disableDuplicateMenu() {
		return this.eorderWizardService.getProductCount() >= 100;
	}

	duplicateProduct(index: number) {
		if (this.eorderWizardService.getProductCount() < 100) {
			const productsControl = <FormArray> this.form.controls['products'];
			const productForm = <FormProduct> productsControl.controls[index];
			const steelGradeControl = <FormControl>productForm.get('steelGrade');
			const thicknessControl = <FormControl>productsControl.controls[index].get('thicknessValue');
			const widthControl = <FormControl>productsControl.controls[index].get('widthValue');
			const quantityControl = <FormControl>productsControl.controls[index].get('quantity');
			const rddTypeControl = <FormControl>productsControl.controls[index].get('rddType');
			const rddControl = <FormControl>productsControl.controls[index].get('rdd');
			const shipToPartyControl = <FormControl>productsControl.controls[index].get('shipToParty');
			const businessTypeControl = <FormControl>productsControl.controls[index].get('businessType');
			const transportModeControl = <FormControl>productsControl.controls[index].get('transportMode');
			const serviceLevelControl = <FormControl>productsControl.controls[index].get('serviceLevel');
			const xcarbControl = <FormControl>productsControl.controls[index].get('xcarb');
			const configurationControl = <FormControl> productsControl.controls[index].get('configurationNumber');
			const a2citemNumberControl = <FormControl> productsControl.controls[index].get('a2citemNumber');
			const simulationStatusControl = <FormControl> productsControl.controls[index].get('isSimulationCompleted');
			const configErrMsgControl = <FormControl> productsControl.controls[index].get('configErrMsg');
			const loaderControl = <FormControl> productsControl.controls[index].get('loader');
			const { productType } = productForm.getProduct();
			const { templateNumber } = productForm.getProduct();
			const { steelGradeDescription } = productForm.getProduct();
			const newVariableFormGroup = new FormGroup({});
			if (templateNumber !== undefined) {
				const variableFormGroup = <FormGroup> productsControl.controls[index].get('variableFormGroup');
				for (const variableKey of Object.keys(variableFormGroup.controls)) {
					const variableControl = variableFormGroup.get(variableKey);
					if (variableControl instanceof FormControl) {
						if (variableControl.hasValidator(Validators.required)) {
							newVariableFormGroup.addControl(
								variableKey,
								new FormControl(variableControl.value, Validators.required)
							);
						} else {
							newVariableFormGroup.addControl(variableKey, new FormControl(variableControl.value));
						}
					} else if (variableControl instanceof FormArray) {
						const formArray = new FormArray<FormControl>([]);
						for (const control of variableControl.controls) {
							if (control.hasValidator(Validators.required)) {
								formArray.push(new FormControl(control.value, Validators.required));
							} else {
								formArray.push(new FormControl(control.value));
							}
						}
						newVariableFormGroup.addControl(variableKey, formArray);
					}
				}
			}
			const eorderProduct: EorderProduct = {
				a2citemNumber: <string>a2citemNumberControl.value,
				configurationNumber: <number>configurationControl.value,
				quantity: <number>quantityControl.value,
				steelGrade: <string>steelGradeControl.value,
				thicknessValue: <number>thicknessControl.value,
				widthValue: <number>widthControl.value,
				rddType: <string>rddTypeControl.value,
				rdd: <string>rddControl.value,
				shipToParty: <string>shipToPartyControl.value,
				businessType: <string>businessTypeControl.value,
				transportMode: <string>transportModeControl.value,
				serviceLevel: <string>serviceLevelControl.value,
				xcarb: <string>xcarbControl.value,
				isSimulationCompleted: <string>simulationStatusControl.value,
				configErrMsg: <string>configErrMsgControl.value,
				loader: <string>loaderControl.value,
				templateNumber,
				productType,
				steelGradeDescription
			};
			if (this.isSheet) {
				const lengthValueControl = <FormControl>productsControl.controls[index].get('lengthValue');
				eorderProduct.lengthValue = <number>lengthValueControl.value;
			}
			this.eorderWizardService.getStateEorderForm().getFormProducts()
				.addProduct(eorderProduct, this.form.getFormValue() || '', 1, [newVariableFormGroup]);
			this.isExpanded[this.isExpanded.length] = true;
			this.isTmodeUpdated[this.isTmodeUpdated.length] = false;
			this.isShipToUpdated[this.isShipToUpdated.length] = false;
			this.isServiceLevelUpdated[this.isServiceLevelUpdated.length] = false;
			this.isxcarbParamUpdated[this.isxcarbParamUpdated.length] = false;
			this.isxcarbParamUpdated[this.isxcarbParamUpdated.length] = false;
			this.isBusinessTypeUpdated[this.isBusinessTypeUpdated.length] = false;
			this.isRddUpdated[this.isRddUpdated.length] = false;
			this.isRddTypeUpdated[this.isRddTypeUpdated.length] = false;
			this.isVariableFieldUpdatedArr[this.isVariableFieldUpdatedArr.length] = new Map<string, boolean>();
		}
	}

	/**
	 * NonFullyCongifured and configData available - resets to its configValues.
	 * NonFullyCongifured and configData not available - resets all editable fields to empty.
	 * FullyConfigured - reset all editable fields to empty.
	 * @param index - The index of the product to reset.
	 */
	resetProduct(index: number) {
		const controlArray = <FormArray> this.form.controls['products'];
		const configurationControl = <FormControl> controlArray.controls[index].get('configurationNumber');
		const configurationNumber = String(configurationControl.value);
		let configData: ProductConfiguration | undefined;
		if (configurationNumber && this.eorderWizardService.getState().ui.hasConfigurationData(configurationNumber)) {
			configData = this.eorderWizardService.getState().ui.getConfigurationData(configurationNumber);
		}
		const templateNumber = this.getTemplateNumber(index);
		const productForm = <FormProduct> controlArray.controls[index];
		let templateDetails: TemplateDescription | undefined;
		if (templateNumber) {
			templateDetails = this.eorderWizardService.getState().ui.getTemplateNumberDetails(
				templateNumber
			);
		}
		const serviceLevelDefault = this.getDefaultServiceLevel();
		const defaultTMmodes = this.tModes.filter(e => e.isDefault);
		const defaultXCarb = this.xcarbParamList.filter(e => e.isDefault);
		const defaultBusinessType = [
			{
				businessCode: '',
				businessDescription: 'None'
			}
		];
		if (defaultTMmodes.length > 0) {
			productForm.product.transportMode = defaultTMmodes[0].code;
			productForm.getFormTransportMode().setValue(defaultTMmodes[0].code);
		} else {
			productForm.product.transportMode = undefined;
			productForm.getFormTransportMode().setValue('');
		}
		if (defaultXCarb.length > 0) {
			productForm.getFormXCarb().setValue(defaultXCarb[0].code);
		} else {
			productForm.getFormXCarb().setValue('');
		}

		if (defaultBusinessType.length > 0) {
			productForm.getFormBusinessType().setValue(defaultBusinessType[0].businessCode);
		} else {
			productForm.getFormBusinessType().setValue('');
		}

		if (serviceLevelDefault && typeof serviceLevelDefault === 'string') {
			productForm.product.serviceLevel = serviceLevelDefault;
			productForm.getFormServiceLevel().setValue(serviceLevelDefault);
		} else {
			productForm.product.serviceLevel = undefined;
			productForm.getFormServiceLevel().setValue('');
		}

		productForm.product.quantity = undefined;
		productForm.product.rddType = 'W';
		productForm.product.rdd = undefined;

		productForm.getFormQuantity().setValue('');
		productForm.getFormRdd().setValue('');
		productForm.getFormRddType().setValue('W');

		if (productForm.product.productType !== PRODUCT_TYPE_F) {
			// NonFullyConfigured products
			productForm.product.shipToParty = undefined;

			productForm.getFormShipToParty().setValue(undefined);

			if (configData && templateDetails && productForm.product.productType !== PRODUCT_TYPE_NF) {
				// resetting to config values
				const shipToDefault = this.eorderWizardService.getDefaultShipTo(configData.shipToParty);
				productForm.product.steelGrade = configData.steelGradeCode;
				if (configData.steelGrade) {
					productForm.product.steelGradeDescription = this.productCharacteristicsTranslationService
						.translate(configData.steelGrade);
				}
				if (shipToDefault && typeof shipToDefault === 'string') {
					productForm.product.shipToParty = shipToDefault;
					productForm.getFormShipToParty().setValue(shipToDefault);
				}
				productForm.product.thicknessValue = configData.thicknessValue;
				productForm.product.widthValue = configData.widthValue;
				productForm.product.lengthValue = configData.lengthValue;
				productForm.getFormSteelGrade().setValue(configData.steelGradeCode);
				productForm.getFormThickness().setValue(configData.thicknessValue);
				productForm.getFormWidth().setValue(configData.widthValue);
				productForm.getFormLength().setValue(configData.lengthValue);

				this.resetProductVariables(productForm, configData, templateDetails); // third row variables reset
				this.updateBeforeSimulationCall(index);
				this.prepareDataAndSimulateA2Cproduct(index);
			} else {
				// resetting to undefined
				productForm.product.steelGrade = undefined;
				productForm.product.steelGradeDescription = undefined;
				productForm.product.thicknessValue = undefined;
				productForm.product.widthValue = undefined;
				productForm.product.lengthValue = undefined;
				productForm.product.isSimulationCompleted = '';
				productForm.product.configErrMsg = '';

				productForm.getFormSteelGrade().setValue(undefined);
				productForm.getFormThickness().setValue(undefined);
				productForm.getFormWidth().setValue(undefined);
				productForm.getFormLength().setValue(undefined);
				productForm.getSimulationCompleted().setValue('');
				productForm.getConfigErrMsg().setValue('');

				if (configData && templateDetails && productForm.product.productType === PRODUCT_TYPE_NF) {
					this.resetProductVariables(productForm, configData, templateDetails); // third row variables reset
				} else {
					for (const key of Object.keys(productForm.getVariableFormGroup().controls)) {
						const control = productForm.getVariableFormGroup().get(key);
						if (control) {
							this.emptyProductVariableData(control);
						}
					}
				}
			}
		}
		productForm.setErrors(null);
		productForm.markAsUntouched();
	}

	onApplyAll(dataMap: Map<string, string>) {
		for (let i = 0; i < this.form.getFormProducts().length; i++) {
			const controlArray = <FormArray> this.form.get('products');
			for (const [key, value] of dataMap.entries()) {
				const control = <FormControl>controlArray.controls[i].get(key);
				if (control.value !== value) {
					let update = true;
					switch (key) {
					case FIELD_SHIPTO_PARTY:
						if (!this.isVariable(i, FIELD_SHIPTO_PARTY)) {
							update = false;
						} else {
							this.isShipToUpdated[i] = true;
						}
						break;
					case 'businessType':
						this.isBusinessTypeUpdated[i] = true;
						break;
					case 'transportMode':
						this.isTmodeUpdated[i] = true;
						break;
					case 'rdd':
						this.isRddUpdated[i] = true;
						break;
					case 'rddType':
						this.isRddTypeUpdated[i] = true;
						break;
					case 'serviceLevel':
						this.isServiceLevelUpdated[i] = true;
						break;
					case 'xcarb':
						this.isxcarbParamUpdated[i] = true;
						break;
					}
					if (update) {
						control.setValue(value);
					}
				}
			}
			if (this.isShipToUpdated[i]) {
				this.updatePackageType(i);
				this.updateBeforeSimulationCall(i);
				this.prepareDataAndSimulateA2Cproduct(i);
			}
			if (this.isShipToUpdated[i] || this.isBusinessTypeUpdated[i]
				|| this.isTmodeUpdated[i]
				|| this.isServiceLevelUpdated[i] || this.isxcarbParamUpdated[i]) {
				this.isExpanded[i] = true;
			}
		}

		setTimeout(() => {
			for (let i = 0; i < this.isTmodeUpdated.length; i++) {
				this.isTmodeUpdated[i] = false;
				this.isShipToUpdated[i] = false;
				this.isServiceLevelUpdated[i] = false;
				this.isxcarbParamUpdated[i] = false;
				this.isBusinessTypeUpdated[i] = false;
				this.isRddUpdated[i] = false;
				this.isRddTypeUpdated[i] = false;
			}
		}, 3000);
	}

	onShipToChange(formIndex: number) {
		this.updatePackageType(formIndex);
		this.simulationCallOnValueChange(formIndex);
	}

	updatePackageType(formIndex: number) {
		const controlArray = <FormArray> this.form.get('products');
		const deliveryAddress = <FormControl> controlArray.controls[formIndex].get('shipToParty');
		const rdd = <FormControl> controlArray.controls[formIndex].get('rdd');
		const variableFormControl = <FormControl> controlArray.controls[formIndex]
			.get('variableFormGroup')
			?.get('winterSummer');
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (deliveryAddress.value && rdd.value && variableFormControl) {
			const deliveryAddressPadded = String(deliveryAddress.value).padStart(10, '0');
			const partnerInfo = this.eorderWizardService.getPartnerInfo(deliveryAddressPadded);
			const rddDate = new Date(<string>rdd.value);
			const day = String(rddDate.getDate()).padStart(2, '0');
			const month = String(rddDate.getMonth() + 1).padStart(2, '0');
			const formattedRdd = `${ day }.${ month }`;
			if (partnerInfo) {
				const summerFrom = partnerInfo.customerPackagingPeriodSummerFromPeriod?.trim();
				const summerTo = partnerInfo.customerPackagingPeriodSummerToPeriod?.trim();
				const winterFrom = partnerInfo.customerPackagingPeriodWinterFromPeriod?.trim();
				const winterTo = partnerInfo.customerPackagingPeriodWinterToPeriod?.trim();

				const hasSummer = Boolean(summerFrom) && Boolean(summerTo);
				const hasWinter = Boolean(winterFrom) && Boolean(winterTo);

				if (hasSummer || hasWinter) {
					if (hasSummer) {
						if (this.isDateInRange(formattedRdd, summerFrom || '', summerTo || '')) {
							variableFormControl.setValue('S');
						} else {
							variableFormControl.setValue('W');
						}
					} else if (hasWinter) {
						if (this.isDateInRange(formattedRdd, winterFrom || '', winterTo || '')) {
							variableFormControl.setValue('W');
						} else {
							variableFormControl.setValue('S');
						}
					}
				} else {
					variableFormControl.setValue('');
				}
			}
		}
	}

	isDateInRange(date: string, start: string, end: string): boolean {
		if (!start || !end || start.trim() === '' || end.trim() === '') {
			return false;
		}
		const dateValue = this.convertToComparableFormat(date);
		const startValue = this.convertToComparableFormat(start);
		const endValue = this.convertToComparableFormat(end);

		return startValue > endValue
			? dateValue >= startValue || dateValue <= endValue
			: dateValue >= startValue && dateValue <= endValue;
	}

	convertToComparableFormat = (dateStr: string): number => {
		const [day, month] = dateStr.split('.').map(Number);
		return month * 100 + day;
	};

	getVariableFieldUpdatedState(index: number, variableName: string, multiInputIndex?: number) {
		if (this.isVariableFieldUpdatedArr[index]) {
			const variableFieldUpdated = this.isVariableFieldUpdatedArr[index];
			if (multiInputIndex && Array.isArray(variableFieldUpdated) && variableFieldUpdated[multiInputIndex]) {
				return true;
			} else if (variableFieldUpdated.has(variableName)) {
				return variableFieldUpdated.get(variableName);
			}
		}
		return false;
	}

	getCurrencyDisplay(code: string): string {
		if (this.currencyList && this.currencyList.length > 0) {
			const currency = this.currencyList.find(c => c.code === code);
			return currency ? `${ currency.code } - ${ currency.description }` : code;
		}
		return code;
	}

	private handleError(message: string, err: unknown) {
		this.templatesInLoading.clear();
		console.error(message, err);
		this.eorderWizardService.showError();
	}

	private loadVariableValues(
		templateNumber: number,
		variableName: string,
		templateVariableValues: TemplateVariableValues
	) {
		if (this.eorderWizardService.checkVariableValuesExistByTemplateNumberAndVariableName(
			templateNumber,
			variableName
		)) {
			return of(true);
		}
		if (templateVariableValues.freeType || templateVariableValues.type === 'number') {
			this.prepareVariableValuesAgainstTemplateNumber(
				templateNumber,
				variableName,
				templateVariableValues
			);
			return of(true);
		}
		return this.configurationService.getVariableValues(templateNumber, variableName)
			.pipe(
				tap(values => {
					values.freeType = templateVariableValues.freeType;
					values.maxItems = templateVariableValues.maxItems;
					values.type = templateVariableValues.type;
					values.parentVariableName = templateVariableValues.parentVariableName;
					values.group = templateVariableValues.group;
					values.order = templateVariableValues.order;
					values.hidden = templateVariableValues.hidden;
					this.prepareVariableValuesAgainstTemplateNumber(
						templateNumber,
						variableName,
						values
					);
				}),
				map(() => true),
				catchError((err: unknown) => {
					console.error(
						`unable to load values for variable '${ variableName }' of template '${ templateNumber }'`,
						err
					);
					return of(false);
				})
			);
	}

	private prepareVariableValuesAgainstTemplateNumber(
		templateNumber: number,
		variableName: string,
		values: TemplateVariableValues
	) {
		let templateVariableValues = this.eorderWizardService.variableValuesIndexedByTemplateNumber
			.get(templateNumber);
		if (templateVariableValues === undefined) {
			templateVariableValues = new Map<string, TemplateVariableValues>();
			this.eorderWizardService.variableValuesIndexedByTemplateNumber
				.set(templateNumber, templateVariableValues);
		}
		templateVariableValues.set(variableName, values);
	}

	private prepareDefaultValuesForApplyForAll() {
		this.inputForApplyAll.set('transportMode', '');
		this.inputForApplyAll.set('shipToParty', '');
		this.inputForApplyAll.set('serviceLevel', '');
		this.inputForApplyAll.set('xcarb', '');
		this.inputForApplyAll.set('rddType', '');
		this.inputForApplyAll.set('businessType', '');
		this.inputForApplyAll.set('rdd', '');

		const formProducts = this.form.getFormProducts();
		const controlArray = <FormArray> formProducts;

		for (let i = 0; i < controlArray.length; i++) {
			for (const [key, value] of this.inputForApplyAll.entries()) {
				if (key === FIELD_SHIPTO_PARTY && !this.isVariable(i, FIELD_SHIPTO_PARTY)) {
					continue;
				}
				const control = <FormControl>controlArray.controls[i].get(key);
				if (control.value !== value) {
					if (value === '') {
						this.inputForApplyAll.set(key, String(control.value));
					} else {
						this.inputForApplyAll.delete(key);
					}
				}
			}
		}
	}

	private removeTranslationPrefix(value: string): string {
		return value.slice(Math.max(0, value.lastIndexOf('.') + 1));
	}

	/**
	 * Resets third row product variables to their configuration values.
	 * @param productForm - The form containing the product variables.
	 * @param configData - The configuration data for the product.
	 * @param templateDetails - The template details for the product.
	 */
	private resetProductVariables(
		productForm: FormProduct,
		configData: ProductConfiguration,
		templateDetails: TemplateDescription
	) {
		for (const key of Object.keys(productForm.getVariableFormGroup().controls)) {
			const variable = templateDetails.properties[key];
			const control = productForm.getVariableFormGroup().get(key);
			if (control) {
				if (productForm.product.productType !== PRODUCT_TYPE_NF || variable.hidden) {
					if (control instanceof FormArray) {
						this.resetProductVariablesMultiItems(control, configData, templateDetails, key);
					} else if (configData[key] && typeof configData[key] === 'string') {
						control.setValue(this.removeTranslationPrefix(String(configData[key])));
					} else {
						control.setValue(configData[key]);
					}
				} else {
					this.emptyProductVariableData(control);
				}
			}
		}
	}

	private emptyProductVariableData(control: AbstractControl) {
		if (control instanceof FormArray) {
			let isMandatory = false;
			if (control.controls.length > 0) {
				const [formControl] = control.controls;
				if (formControl.hasValidator(Validators.required)) {
					isMandatory = true;
				}
			}
			control.controls = [];
			if (isMandatory) {
				control.controls.push(new FormControl('', Validators.required));
			} else {
				control.controls.push(new FormControl(''));
			}
		} else {
			control.setValue(undefined);
		}
	}

	/**
	 * Resets third row multi-occurrence fields to their configuration values.
	 * If confidData is not available, clearing formArray and adding one item in it with empty value.
	 * if configData available, clearing formArray and pushing number of configData available.
	 * @param formArray - form array containing the product variables.
	 * @param configData - configuration data for the product.
	 * @param templateDetails - template details - get contentChildFieldName and process equalant value from configData.
	 * @param variableName - name of the variable.
	 */
	private resetProductVariablesMultiItems(
		formArray: FormArray,
		configData: ProductConfiguration,
		templateDetails: TemplateDescription,
		variableName: string
	) {
		formArray.controls = []; // clearing formArray items
		const arrayObject: CharacteristicValueSimpleObject[] =
		/**
		 * multioccurrence array value from configData
		 */
		<CharacteristicValueSimpleObject[]> configData[variableName];
		const variable = templateDetails.properties[variableName];
		const variableArray = <TemplateVariableArray>variable;
		this.eorderWizardService.prepareVariableFormGroupArray(
			formArray,
			arrayObject,
			variableArray,
			variable.mandatory
		);
	}

	private setDefaultValue(controlName: string, index: number, defaultValue: string) {
		const formProducts = this.form.getFormProducts();
		const controlArray = <FormArray> formProducts;
		const control = <FormControl> controlArray.controls[index].get(controlName);
		if (control.value === '' || control.value === null) {
			control.setValue(defaultValue);
		}
	}

	private loadAllVariableValuesByType(
		template: TemplateDescription,
		variableName: string,
		variable: TemplateVariable,
		variableValueObservables: Observable<boolean>[],
		group?: string,
		order?: number
	) {
		const freeType = (<TemplateVariableString> variable).free;
		const templateVariableValues: TemplateVariableValues = {
			type: variable.type,
			values: [],
			freeType,
			maxItems: undefined,
			parentVariableName: variableName,
			mandatory: variable.mandatory,
			hidden: variable.hidden,
			group,
			order
		};
		switch (variable.type) {
		case 'number':
		case 'integer':
		case 'string':
			variableValueObservables.push(this.loadVariableValues(
				template.templateNumber,
				variableName,
				templateVariableValues
			));
			break;
		case 'array':
			{
				const variableArray = <TemplateVariableArray>variable;
				for (const entryVariableName of Object.getOwnPropertyNames(variableArray.properties)) {
					const variableLocal = variableArray.properties[entryVariableName];
					const freeTypeLocal = (<TemplateVariableString> variableLocal).free;
					templateVariableValues.freeType = freeTypeLocal;
					templateVariableValues.maxItems = variableArray.maxItems;
					variableValueObservables.push(this.loadVariableValues(
						template.templateNumber,
						entryVariableName,
						templateVariableValues
					));
				}
			}
			break;
		case 'group':
			this.processGroupVariable(template, variable, variableValueObservables, variableName);
			break;
		}
	}

	private processGroupVariable(
		template: TemplateDescription,
		groupVariable: TemplateVariable,
		variableValueObservables: Observable<boolean>[],
		group: string
	) {
		if (groupVariable.properties) {
			for (const variableName of Object.getOwnPropertyNames(groupVariable.properties)) {
				const variable = groupVariable.properties[variableName];
				this.loadAllVariableValuesByType(
					template,
					variableName,
					variable,
					variableValueObservables,
					group,
					variable.order
				);
			}
		}
	}

	private updateSimulationData(index: number, data: ProductConfiguration) {
		const controlArray = <FormArray> this.form.get('products');
		const formProduct = <FormProduct> controlArray.controls[index];
		const product = formProduct.getProduct();
		const templateNumber = this.getTemplateNumber(index);
		if (!templateNumber) {
			return;
		}
		// first and second row update
		const valuesPossiblePerField = this.getValuesPossiblePerField(templateNumber);
		const firstRowVariableFields = this.filterVariablesFirstRow(valuesPossiblePerField);
		for (const field of FIRST_ROW_FIELDS) {
			const formGroupcontrol = controlArray.controls[index].get(field);
			if (formGroupcontrol && data[field]) {
				const control = <FormControl>formGroupcontrol;
				const variableField = firstRowVariableFields.find(i => i.keys === field);
				if (field === FIELD_SHIPTO_PARTY) {
					data.shipToParty = data.shipToParty?.replace(/^0+/, '');
				}
				if (variableField) {
					this.updateSimulationValueInFormControl(
						variableField,
						variableField.type === 'number'
							? Number(data[variableField.keys]) : <string> data[variableField.keys],
						control,
						variableField.type,
						index
					);
				} else {
					// not a variable
					let value;
					if (field === FIELD_SHIPTO_PARTY || field === FIELD_STEEL_GRADE) {
						const simulationValue = String(data[field]);
						value = this.removeTranslationPrefix(simulationValue);
						if (field === FIELD_STEEL_GRADE) {
							product.steelGradeDescription = this.productCharacteristicsTranslationService
								.translate(String(simulationValue));
						}
					} else {
						value = Number(data[field]);
					}
					if (control.value !== value) {
						control.setValue(value);
						this.variableFieldUpdatedStateChange(index, field);
					}
				}
			}
		}

		// third row update
		const thirdRowVariableFields = this.eorderWizardService.getVariablesForThirdRow(templateNumber);
		for (const variableField of thirdRowVariableFields) {
			const formGroupcontrol = controlArray.controls[index]
				.get('variableFormGroup')?.get(variableField.keys);
			if (formGroupcontrol && (data[variableField.keys] || data[variableField.parentVariableName])) {
				if (variableField.type === 'array') {
					let arrayObject: CharacteristicValueSimpleObject[] = [];
					if (data[variableField.parentVariableName]) {
						arrayObject = <CharacteristicValueSimpleObject[]> data[variableField.parentVariableName];
					}
					const formArray = <FormArray> formGroupcontrol;
					let i = 0;
					for (const obj of arrayObject) {
						if (formArray.controls[i].value !== obj[variableField.keys]) {
							this.updateSimulationValueInFormControl(
								variableField,
								String(obj[variableField.keys]),
								<FormControl>formArray.controls[i],
								'string',
								index,
								i
							);
						}
						i++;
					}
				} else {
					this.updateSimulationValueInFormControl(
						variableField,
						variableField.type === 'number'
							? Number(data[variableField.keys]) : <string> data[variableField.keys],
						<FormControl>formGroupcontrol,
						variableField.type,
						index
					);
				}
			}
		}

		// tolerance row update
		const optionalToleranceGroup = this.eorderWizardService.getOptionalToleranceFields(templateNumber);
		for (const [, variableFields] of optionalToleranceGroup.entries()) {
			for (const variableField of variableFields) {
				const formGroupControl = controlArray.controls[index]
					.get('variableFormGroup')?.get(variableField.keys);

				if (formGroupControl && data[variableField.keys]) {
					this.updateSimulationValueInFormControl(
						variableField,
						variableField.type === 'number'
							? Number(data[variableField.keys]) : <string> data[variableField.keys],
						<FormControl>formGroupControl,
						variableField.type,
						index
					);
				}
			}
		}
		setTimeout(() => {
			this.variableFieldUpdatedStateReset();
		}, 3000);
	}

	private updateSimulationValueInFormControl(
		variableField: VariableField,
		simulationValue: number | string,
		control: FormControl,
		type: string,
		index: number,
		multiInputIndex?: number
	) {
		if (type === 'number' && control.value !== simulationValue) {
			control.setValue(simulationValue);
			this.variableFieldUpdatedStateChange(index, variableField.keys, multiInputIndex);
		} else if (type === 'string') {
			const value = this.removeTranslationPrefix(String(simulationValue));
			if (control.value !== value) {
				control.setValue(value);
				this.variableFieldUpdatedStateChange(index, variableField.keys, multiInputIndex);
				if (!variableField.freeType && !variableField.values.some(v => v.code === value)) {
					// include in list
					const displayValue = this.productCharacteristicsTranslationService
						.translate(String(simulationValue));
					const newValue: CodeValueType = { code: value, value: displayValue };
					variableField.values.push(newValue);
				}
			}
		}
	}

	private updateSimulationStatus(index: number, errors?: string[], error?: string) {
		const controlArray = <FormArray> this.form.get('products');
		const formProduct = <FormProduct> controlArray.controls[index];
		const loaderControl = <FormControl> formProduct.get('loader');
		if (loaderControl.value === 'true') {
			const simulationCompletedFlagControl = <FormControl> formProduct.get('isSimulationCompleted');
			const configErrMsgControl = <FormControl>controlArray.controls[index].get('configErrMsg');
			if (errors && errors.length > 0) {
				configErrMsgControl.setValue(errors);
			} else if (error) {
				configErrMsgControl.setValue(error);
			}
			if (configErrMsgControl.value) {
				simulationCompletedFlagControl.setValue('');
			} else {
				simulationCompletedFlagControl.setValue('true');
			}
			loaderControl.setValue('false');
			this.changeDetectorRef.detectChanges();
		}
	}

	private updateBeforeSimulationCall(index: number) {
		const controlArray = <FormArray> this.form.get('products');
		const formProduct = <FormProduct> controlArray.controls[index];
		formProduct.getSimulationCompleted().setValue('');
		formProduct.getConfigErrMsg().setValue('');
		formProduct.getLoader().setValue('false');
		formProduct.product.isSimulationCompleted = '';
		formProduct.product.configErrMsg = '';
		formProduct.product.loader = 'false';
	}

	private variableFieldUpdatedStateChange(index: number, variableName: string, multiInputIndex?: number) {
		const variableFieldUpdateState = this.isVariableFieldUpdatedArr[index];
		if (multiInputIndex) {
			let multiInputFieldUpdated = variableFieldUpdateState.get(variableName);
			if (Array.isArray(multiInputFieldUpdated)) {
				multiInputFieldUpdated[multiInputIndex] = true;
			} else {
				multiInputFieldUpdated = [];
				multiInputFieldUpdated[multiInputIndex] = true;
				variableFieldUpdateState.set(variableName, multiInputFieldUpdated);
			}
		} else {
			variableFieldUpdateState.set(variableName, true);
		}
	}

	private variableFieldUpdatedStateReset() {
		for (const variableFieldUpdated of this.isVariableFieldUpdatedArr) {
			for (const [key, value] of variableFieldUpdated.entries()) {
				if (Array.isArray(value)) {
					for (let i = 0; i < value.length; i++) {
						value[i] = false;
					}
				} else {
					variableFieldUpdated.set(key, false);
				}
			}
		}
	}

	private setProperty<T, K extends keyof T>(obj: T, key: K, value: CharacteristicValue) {
		obj[key] = <T[K]>value;
		return obj;
	}

	private bocAvailableValues(parameter: string) {
		return this.userService.getBocAvailableValuesBasedOnCode('EORDR', parameter);
	}
}
